import { useNavigate, useParams } from 'react-router';

import { ChevronRightIcon, Footer, TitleBar } from '../../../../components';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import QueryErrorBoundary from '../../../../components/error-handling';
import GenericListManager from '../../../../components/generic/ListManager/GenericListManager';

import { FETCH_IT_APPLICATIONS } from '../../../dataInputs/queries/itApplications';

import { UPDATE_LOCATION_IT_APPS } from './queries';
import { RemoveITApplicationModal } from '../../components/RemoveITApplicationModal';
import { Button } from 'antd';
import { useLocationName } from '../../../../hooks';
import {
  useFetchSelectedLocationITApplications,
  useRemoveITApplicationConfirmation,
} from '../../hooks';
import { FETCH_LOCATION_SELECTED_IT_APPS } from '../../hooks/queries';

const LocationApplicationSelection = () => {
  const { locationId } = useParams();

  const { locationName, locationNameError } = useLocationName(locationId);

  const navigate = useNavigate();

  const { data: applications } =
    useFetchSelectedLocationITApplications(locationId);

  const {
    isModalOpen,
    affectedApplications,
    handleBeforeUpdate,
    handleConfirm,
    handleCancel,
  } = useRemoveITApplicationConfirmation(
    applications?.getSelectedLocationItApplications ?? [],
  );

  return (
    <QueryErrorBoundary error={locationNameError}>
      <TitleUpdater title={`${locationName}/IT Application Selection`} />
      <TitleBar
        title={`Location Application Dependency Mapping`}
        subtitle={`${locationName} Application Selection`}
        brief={`Select which IT applications directly support revenue at the ${locationName} location. Exact percentages will be captured on the next page. If an IT application is missing, it needs to be added in the data inputs section.`}
      />
      <GenericListManager
        addedTitle="IT Applications Added"
        availableTitle="IT Applications Available"
        fetchAllQuery={{
          query: FETCH_IT_APPLICATIONS,
          identifier: 'findAllItApplications',
        }}
        fetchSelectedQuery={{
          query: FETCH_LOCATION_SELECTED_IT_APPS,
          identifier: 'getSelectedLocationItApplications',
          variables: {
            data: locationId,
          },
        }}
        refetchQueries={[
          {
            query: FETCH_LOCATION_SELECTED_IT_APPS,
            variables: {
              data: locationId,
            },
          },
        ]}
        itemKey="uuid"
        itemLabel="application"
        updateMutation={{
          mutation: UPDATE_LOCATION_IT_APPS,
          variableName: 'data.itApplications',
          variableStructure: {
            data: {
              location: locationId,
            },
          },
        }}
        beforeUpdate={(items) => {
          return new Promise((resolve) => {
            handleBeforeUpdate(items, () => resolve(true));
          });
        }}
        isLocation={!!locationName}
      />
      <Footer
        backPath={`/cyber/location-level/${locationId}/application-mapping`}
        nextPath={`/cyber/location-level/${locationId}/it-applications/mapping`}
      />
      <RemoveITApplicationModal
        isOpen={isModalOpen}
        onCancel={handleCancel}
        onRemove={handleConfirm}
        applications={affectedApplications}
        type="Percentage"
        extendActions={
          <Button
            type="link"
            size="small"
            onClick={() =>
              navigate(
                `/cyber/location-level/${locationId}/it-applications/mapping`,
              )
            }
            className="group flex items-center p-0 h-auto"
          >
            <div className="flex items-center gap-4">
              View Applications
              <ChevronRightIcon
                width={8}
                height={14}
                className="group-hover:opacity-50 hover:!fill-[#437EF7]"
              />
            </div>
          </Button>
        }
      />
    </QueryErrorBoundary>
  );
};

export default LocationApplicationSelection;
